import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/utils/api';
import { Box, Brain, Loader2, TrendingUp, Wrench, Home, DollarSign, AlertTriangle, CheckCircle2, X, Zap, Activity, ShieldCheck, ChevronRight } from 'lucide-react';
import Layout from '@/components/Layout';

const SCENARIOS = [
  { id: 'preventive', priority: 'medium', cost: 0.005, riskCut: 35, name_en: 'Preventive Maintenance Plan', name_ar: 'خطة صيانة وقائية', desc_en: 'Schedule quarterly inspections + early-replace HVAC filters and lift cables.', desc_ar: 'فحوصات ربع سنوية + استبدال مبكر لفلاتر التكييف وكابلات المصاعد.' },
  { id: 'urgent_repair', priority: 'high', cost: 0.012, riskCut: 60, name_en: 'Urgent Critical Repair', name_ar: 'إصلاح طارئ', desc_en: 'Immediately fix high-risk defects (water leaks, electrical, structural cracks).', desc_ar: 'إصلاح فوري للأعطال الحرجة (تسربات، أعطال كهرباء، تشققات إنشائية).' },
  { id: 'green_retrofit', priority: 'low', cost: 0.025, riskCut: 20, name_en: 'Energy Retrofit', name_ar: 'تحديث للطاقة', desc_en: 'Insulation, LED lighting, smart meters — payback ~3 years, ~18% utility savings.', desc_ar: 'عزل حراري، إضاءة LED، عدادات ذكية — عائد ~3 سنوات، توفير ~18%.' },
  { id: 'cosmetic', priority: 'low', cost: 0.003, riskCut: 8, name_en: 'Cosmetic Refresh', name_ar: 'تجديد تجميلي', desc_en: 'Repaint, refresh lobbies & signage — boost rent achievable by 4–7%.', desc_ar: 'إعادة طلاء، تجديد المداخل واللوحات — رفع الإيجار 4–7%.' },
];

const HEALTH_LABEL = (s, isAr) => s >= 75 ? (isAr ? 'سليم' : 'Healthy') : s >= 50 ? (isAr ? 'تحذير' : 'Warning') : (isAr ? 'حرج' : 'Critical');

export default function DigitalTwin() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [pickedScenario, setPickedScenario] = useState(null);
  const [aiSimulating, setAiSimulating] = useState(false);
  const [aiSimResult, setAiSimResult] = useState(null);

  useEffect(() => { fetchBuildings(); }, []);

  const fetchBuildings = async () => {
    try { const res = await api.getDigitalTwinBuildings(); setBuildings(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const runAISim = async (buildingId) => {
    setAiSimulating(true); setAiSimResult(null);
    try {
      const res = await api.simulateMaintenance(buildingId);
      setAiSimResult(res.data);
    } catch (e) { setAiSimResult({ error: 'AI simulation failed' }); }
    finally { setAiSimulating(false); }
  };

  const getHealthColor = (s) => s >= 75 ? 'text-green-600 bg-green-50 border-green-200' : s >= 50 ? 'text-yellow-600 bg-yellow-50 border-yellow-200' : 'text-red-600 bg-red-50 border-red-200';
  const getHealthBar = (s) => s >= 75 ? 'bg-green-500' : s >= 50 ? 'bg-yellow-500' : 'bg-red-500';
  const getHealthIcon = (s) => s >= 75 ? <CheckCircle2 className="h-3.5 w-3.5" /> : s >= 50 ? <AlertTriangle className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center"><Box className="h-5 w-5 text-violet-600" strokeWidth={1.5} /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'التوأم الرقمي' : 'Digital Twin'}</h1>
            <p className="text-sm text-gray-500">{isAr ? 'ملفات رقمية حية للمباني مع محاكاة سيناريوهات الصيانة' : 'Live building digital profiles with AI maintenance simulation'}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div></div>
        ) : buildings.length === 0 ? (
          <Card className="shadow-soft border-gray-100"><CardContent className="py-12 text-center text-gray-400"><Box className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>{isAr ? 'لا توجد مباني — ابدأ بإضافة عقار' : 'No buildings yet — add a property to start'}</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {buildings.map(b => (
              <button key={b.id} onClick={() => setSelected(b)} className="text-start group" data-testid={`twin-card-${b.id}`}>
                <Card className="shadow-soft border-gray-100 hover:shadow-hover transition-all hover:-translate-y-0.5 group-hover:border-primary/30 cursor-pointer h-full">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-heading font-semibold text-gray-900">{isAr ? b.name_ar || b.name : b.name}</h3>
                          <ChevronRight className={`h-4 w-4 text-gray-300 group-hover:text-primary transition-colors ${isAr ? 'rotate-180' : ''}`} />
                        </div>
                        <p className="text-xs text-gray-500">{b.city} · {b.type}</p>
                      </div>
                      <div className={`px-2.5 py-1 rounded-xl border text-xs font-bold flex items-center gap-1 ${getHealthColor(b.health_score)}`}>
                        {getHealthIcon(b.health_score)} {HEALTH_LABEL(b.health_score, isAr)} · {b.health_score}%
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-2 transition-all ${getHealthBar(b.health_score)}`} style={{ width: `${b.health_score}%` }} />
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      <div className="text-center p-2 bg-gray-50 rounded-lg">
                        <Home className="h-3.5 w-3.5 text-blue-600 mx-auto mb-1" />
                        <div className="text-sm font-bold">{b.occupied_units}/{b.total_units}</div>
                        <div className="text-[10px] text-gray-500">{isAr ? 'إشغال' : 'Occupied'}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded-lg">
                        <Wrench className="h-3.5 w-3.5 text-orange-600 mx-auto mb-1" />
                        <div className="text-sm font-bold">{b.open_maintenance}</div>
                        <div className="text-[10px] text-gray-500">{isAr ? 'صيانة' : 'Issues'}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded-lg">
                        <Activity className="h-3.5 w-3.5 text-green-600 mx-auto mb-1" />
                        <div className="text-sm font-bold">{b.avg_condition}</div>
                        <div className="text-[10px] text-gray-500">{isAr ? 'حالة' : 'Score'}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded-lg">
                        <DollarSign className="h-3.5 w-3.5 text-emerald-600 mx-auto mb-1" />
                        <div className="text-sm font-bold">{(b.monthly_revenue / 1000).toFixed(0)}k</div>
                        <div className="text-[10px] text-gray-500">{isAr ? 'إيراد' : 'Rev/mo'}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>
        )}

        {/* Building Detail Drawer */}
        <Dialog open={!!selected && !scenarioOpen} onOpenChange={() => { setSelected(null); setAiSimResult(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selected && (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between pe-6">
                    <div>
                      <DialogTitle className="font-heading text-xl">{isAr ? selected.name_ar || selected.name : selected.name}</DialogTitle>
                      <p className="text-sm text-gray-500 mt-0.5">{selected.city} · {selected.type}</p>
                    </div>
                    <div className={`px-3 py-1.5 rounded-xl border text-sm font-bold flex items-center gap-1.5 ${getHealthColor(selected.health_score)}`}>
                      {getHealthIcon(selected.health_score)} {HEALTH_LABEL(selected.health_score, isAr)} · {selected.health_score}%
                    </div>
                  </div>
                </DialogHeader>

                <div className="space-y-5 mt-4">
                  {/* Health Bar */}
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                      <span>{isAr ? 'صحة المبنى' : 'Building Health Score'}</span>
                      <span className="font-medium">{selected.health_score}/100</span>
                    </div>
                    <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-3 transition-all ${getHealthBar(selected.health_score)}`} style={{ width: `${selected.health_score}%` }} />
                    </div>
                  </div>

                  {/* Detailed metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 bg-blue-50 rounded-xl">
                      <div className="text-[10px] uppercase text-blue-600 font-bold tracking-wider">{isAr ? 'الإشغال' : 'Occupancy'}</div>
                      <div className="text-xl font-bold text-gray-900 mt-0.5">{selected.total_units > 0 ? Math.round((selected.occupied_units / selected.total_units) * 100) : 0}%</div>
                      <div className="text-[10px] text-gray-500">{selected.occupied_units}/{selected.total_units} {isAr ? 'وحدة' : 'units'}</div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-xl">
                      <div className="text-[10px] uppercase text-orange-600 font-bold tracking-wider">{isAr ? 'مخاطر صيانة' : 'Maint. Risk'}</div>
                      <div className="text-xl font-bold text-gray-900 mt-0.5">{selected.open_maintenance > 5 ? (isAr ? 'مرتفع' : 'High') : selected.open_maintenance > 2 ? (isAr ? 'متوسط' : 'Medium') : (isAr ? 'منخفض' : 'Low')}</div>
                      <div className="text-[10px] text-gray-500">{selected.open_maintenance} {isAr ? 'مشاكل مفتوحة' : 'open issues'}</div>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-xl">
                      <div className="text-[10px] uppercase text-emerald-600 font-bold tracking-wider">{isAr ? 'الإيرادات' : 'Revenue'}</div>
                      <div className="text-xl font-bold text-gray-900 mt-0.5">{(selected.monthly_revenue / 1000).toFixed(0)}k</div>
                      <div className="text-[10px] text-gray-500">{isAr ? 'ريال/شهر' : 'SAR/month'}</div>
                    </div>
                    <div className="p-3 bg-violet-50 rounded-xl">
                      <div className="text-[10px] uppercase text-violet-600 font-bold tracking-wider">{isAr ? 'متوسط الحالة' : 'Avg Condition'}</div>
                      <div className="text-xl font-bold text-gray-900 mt-0.5">{selected.avg_condition}/10</div>
                      <div className="text-[10px] text-gray-500">{isAr ? 'مقياس فحص' : 'inspection score'}</div>
                    </div>
                  </div>

                  {/* Recommended Action */}
                  <div className="p-4 bg-gradient-to-br from-primary/5 to-amber-50 rounded-2xl border border-primary/10">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                        <ShieldCheck className="h-4.5 w-4.5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="text-[10px] uppercase font-bold text-primary tracking-wider mb-1">{isAr ? 'إجراء موصى به' : 'Recommended Action'}</div>
                        <p className="text-sm text-gray-800 leading-relaxed">
                          {selected.health_score < 50
                            ? (isAr ? 'اطلب فحصاً عاجلاً للمبنى وابدأ بسيناريو الإصلاح الطارئ.' : 'Schedule an urgent inspection and run the Critical Repair scenario.')
                            : selected.open_maintenance > 3
                            ? (isAr ? 'لديك أكثر من 3 طلبات صيانة مفتوحة — أعطها أولوية هذا الأسبوع.' : 'You have 3+ open maintenance issues — prioritise them this week.')
                            : selected.total_units > 0 && (selected.occupied_units / selected.total_units) < 0.8
                            ? (isAr ? 'الإشغال أقل من 80% — راجع التسعير والتسويق.' : 'Occupancy is below 80% — review pricing and marketing.')
                            : (isAr ? 'المبنى في حالة جيدة. تابع جدول الصيانة الوقائية.' : 'Building is in good shape. Continue preventive maintenance schedule.')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button onClick={() => setScenarioOpen(true)} className="flex-1 gradient-primary text-white rounded-xl" data-testid="twin-simulate-btn">
                      <Brain className="h-4 w-4 me-2" /> {isAr ? 'محاكاة سيناريوهات الصيانة' : 'Simulate Maintenance Scenarios'}
                    </Button>
                    <Button onClick={() => runAISim(selected.id)} disabled={aiSimulating} variant="outline" className="rounded-xl" data-testid="twin-ai-sim-btn">
                      {aiSimulating ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Zap className="h-4 w-4 me-2" />}
                      {isAr ? 'محاكاة AI' : 'AI Simulate'}
                    </Button>
                  </div>

                  {aiSimResult && (
                    <Card className="border-violet-200 bg-violet-50/40">
                      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Brain className="h-4 w-4 text-violet-600" /> {isAr ? 'تحليل AI' : 'AI Analysis'}</CardTitle></CardHeader>
                      <CardContent>
                        <div className="p-3 bg-white rounded-xl text-xs whitespace-pre-wrap font-mono leading-relaxed max-h-64 overflow-y-auto">
                          {aiSimResult.error || (typeof aiSimResult.simulation === 'string' ? aiSimResult.simulation : JSON.stringify(aiSimResult.simulation, null, 2))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Scenarios Modal */}
        <Dialog open={scenarioOpen} onOpenChange={(o) => { setScenarioOpen(o); if (!o) setPickedScenario(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Brain className="h-5 w-5 text-primary" /> {isAr ? 'سيناريوهات الصيانة' : 'Maintenance Scenarios'}</DialogTitle>
            </DialogHeader>
            {selected && (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">{isAr ? 'اختر سيناريو لرؤية التكلفة المتوقعة وتأثير الصحة:' : 'Pick a scenario to see expected cost and health impact:'}</p>
                {SCENARIOS.map(sc => {
                  const annualRev = (selected.monthly_revenue || 0) * 12;
                  const cost = annualRev * sc.cost;
                  const newHealth = Math.min(100, selected.health_score + sc.riskCut);
                  const isPicked = pickedScenario?.id === sc.id;
                  const priColor = sc.priority === 'high' ? 'bg-red-50 text-red-700 border-red-200' : sc.priority === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200';
                  return (
                    <button key={sc.id} onClick={() => setPickedScenario(sc)} className={`w-full text-start p-4 rounded-2xl border-2 transition-all ${isPicked ? 'border-primary bg-primary/5' : 'border-gray-100 hover:border-gray-200 bg-white'}`} data-testid={`scenario-${sc.id}`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">{isAr ? sc.name_ar : sc.name_en}</div>
                          <div className="text-xs text-gray-500 mt-1 leading-relaxed">{isAr ? sc.desc_ar : sc.desc_en}</div>
                        </div>
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-md border ${priColor}`}>
                          {isAr ? (sc.priority === 'high' ? 'عاجل' : sc.priority === 'medium' ? 'متوسط' : 'منخفض') : sc.priority}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-gray-100">
                        <div>
                          <div className="text-[10px] text-gray-400 uppercase tracking-wider">{isAr ? 'تكلفة متوقعة' : 'Est. Cost'}</div>
                          <div className="text-sm font-bold text-gray-900 mt-0.5">{cost.toLocaleString('en-US', { maximumFractionDigits: 0 })} <span className="text-[10px] text-gray-400">SAR</span></div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-400 uppercase tracking-wider">{isAr ? 'تقليل المخاطر' : 'Risk Reduction'}</div>
                          <div className="text-sm font-bold text-green-700 mt-0.5">−{sc.riskCut}%</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-400 uppercase tracking-wider">{isAr ? 'الصحة الجديدة' : 'New Health'}</div>
                          <div className="text-sm font-bold text-primary mt-0.5">{newHealth}%</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
                {pickedScenario && (
                  <div className="flex gap-3 pt-2">
                    <Button onClick={() => { runAISim(selected.id); setScenarioOpen(false); }} className="flex-1 gradient-primary text-white rounded-xl" data-testid="scenario-run">
                      <Brain className="h-4 w-4 me-2" /> {isAr ? 'تشغيل تحليل AI لهذا السيناريو' : 'Run AI Analysis for this Scenario'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
